import { useState } from 'react';
import { Flag, X, Send, CheckCircle, ChevronDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

const FLAG_REASONS = [
  { value: 'wrong_protein',   label: '🥩 Wrong protein / deli meat used as main' },
  { value: 'household_item',  label: '🛒 Non-food / household item as ingredient' },
  { value: 'bad_recipe',      label: '👎 Recipe doesn\'t make culinary sense' },
  { value: 'wrong_price',     label: '💰 Completely wrong cost / price' },
  { value: 'duplicate',       label: '🔁 Same recipe shown repeatedly' },
  { value: 'inappropriate',   label: '🚫 Inappropriate or offensive content' },
  { value: 'other',           label: '💬 Other issue' },
];

interface Props {
  recipeName: string;
  storeName?: string;
  trigger?: 'icon' | 'button';  // icon = small flag icon, button = text button
  onFlagged?: () => void;
}

export default function FlagRecipeButton({ recipeName, storeName, trigger = 'icon', onFlagged }: Props) {
  const { user } = useAuth();
  const [open,     setOpen]     = useState(false);
  const [reason,   setReason]   = useState('bad_recipe');
  const [details,  setDetails]  = useState('');
  const [sending,  setSending]  = useState(false);
  const [sent,     setSent]     = useState(false);
  const [catOpen,  setCatOpen]  = useState(false);

  const reset = () => { setReason('bad_recipe'); setDetails(''); setSent(false); setCatOpen(false); };
  const handleClose = () => { setOpen(false); setTimeout(reset, 300); };

  const handleSubmit = async () => {
    setSending(true);
    try {
      await supabase.from('flagged_recipes').insert({
        recipe_name: recipeName,
        reason,
        details:     details.trim() || null,
        user_id:     user?.id ?? null,
        store_name:  storeName ?? null,
        status:      'pending',
      });
      setSent(true);
      onFlagged?.();
    } catch { /* silently fail */ }
    setSending(false);
  };

  const selectedReason = FLAG_REASONS.find(r => r.value === reason)!;

  return (
    <>
      {/* Trigger */}
      {trigger === 'icon' ? (
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          title="Flag this recipe"
          className="w-7 h-7 rounded-full flex items-center justify-center text-muted-foreground/40 hover:text-red-400 hover:bg-red-400/10 transition-all"
        >
          <Flag className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-red-400 transition-colors py-1"
        >
          <Flag className="w-3.5 h-3.5" />
          Report recipe
        </button>
      )}

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={handleClose}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h2 className="font-bold text-foreground text-base leading-tight flex items-center gap-2">
                  <Flag className="w-4 h-4 text-red-400" />
                  Flag Recipe
                </h2>
                <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-xs">"{recipeName}"</p>
              </div>
              <button onClick={handleClose} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>

            {sent ? (
              <div className="flex flex-col items-center gap-3 px-5 py-10">
                <div className="w-14 h-14 rounded-full bg-red-400/10 flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-red-400" />
                </div>
                <p className="font-semibold text-foreground">Recipe flagged for review</p>
                <p className="text-sm text-muted-foreground text-center">We'll review it and remove it if it doesn't meet our standards.</p>
                <button onClick={handleClose} className="mt-1 px-5 py-2 bg-primary text-primary-foreground rounded-full text-sm font-medium">
                  Done
                </button>
              </div>
            ) : (
              <div className="px-5 pb-5 flex flex-col gap-4">
                {/* Reason picker */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">What's the issue?</p>
                  <div className="relative">
                    <button
                      onClick={() => setCatOpen(!catOpen)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-muted rounded-xl text-sm text-foreground border border-border"
                    >
                      <span>{selectedReason.label}</span>
                      <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${catOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {catOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-10 overflow-hidden">
                        {FLAG_REASONS.map(r => (
                          <button key={r.value} onClick={() => { setReason(r.value); setCatOpen(false); }}
                            className={`w-full text-left px-3 py-2.5 text-sm hover:bg-muted transition-colors ${r.value === reason ? 'text-primary font-medium' : 'text-foreground'}`}>
                            {r.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1.5">
                    More details <span className="text-muted-foreground/50">(optional)</span>
                  </p>
                  <textarea
                    value={details}
                    onChange={e => setDetails(e.target.value)}
                    placeholder="e.g. 'Deli ham used as main for an oven roast' or 'Baby jumper showing as ingredient'"
                    rows={3}
                    className="w-full px-3 py-2.5 bg-muted border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:ring-2 focus:ring-red-400/30"
                  />
                </div>

                {/* Submit */}
                <button
                  onClick={handleSubmit}
                  disabled={sending}
                  className="w-full flex items-center justify-center gap-2 bg-red-500 text-white rounded-xl py-3 text-sm font-semibold disabled:opacity-40 hover:bg-red-600 active:scale-98 transition-all"
                >
                  {sending ? (
                    <span className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {sending ? 'Sending...' : 'Submit Flag'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
